import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../constants/environment';

const getWebGLInfo = () => {
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;

        // Must attach to DOM for WebGL to work
        if (document.body) {
            document.body.appendChild(canvas);
            canvas.style.display = 'none';

            const gl = canvas.getContext('webgl') as WebGLRenderingContext | null
                ?? canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;

            if (gl) {
                const webglInfo = {
                    vendor: gl.getParameter(gl.VENDOR),
                    renderer: gl.getParameter(gl.RENDERER),

                    version: gl.getParameter(gl.VERSION),
                    shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
                    extensions: gl.getSupportedExtensions(),

                    maxVertexAttribs: gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
                    maxVertexUniformVectors: gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
                    maxFragmentUniformVectors: gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
                    maxTextureImageUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
                    maxCombinedTextureImageUnits: gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS),
                    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
                    maxCubeMapTextureSize: gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE),
                    maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),

                    vertexShaderPrecisionHighFloat: (() => {
                        const precision = gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.HIGH_FLOAT);
                        return precision ? {
                            rangeMin: precision.rangeMin,
                            rangeMax: precision.rangeMax,
                            precision: precision.precision
                        } : null;
                    })(),
                    vertexShaderPrecisionMediumFloat: (() => {
                        const precision = gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.MEDIUM_FLOAT);
                        return precision ? {
                            rangeMin: precision.rangeMin,
                            rangeMax: precision.rangeMax,
                            precision: precision.precision
                        } : null;
                    })(),
                    vertexShaderPrecisionLowFloat: (() => {
                        const precision = gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.LOW_FLOAT);
                        return precision ? {
                            rangeMin: precision.rangeMin,
                            rangeMax: precision.rangeMax,
                            precision: precision.precision
                        } : null;
                    })(),
                    fragmentShaderPrecisionHighFloat: (() => {
                        const precision = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
                        return precision ? {
                            rangeMin: precision.rangeMin,
                            rangeMax: precision.rangeMax,
                            precision: precision.precision
                        } : null;
                    })(),
                    fragmentShaderPrecisionMediumFloat: (() => {
                        const precision = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.MEDIUM_FLOAT);
                        return precision ? {
                            rangeMin: precision.rangeMin,
                            rangeMax: precision.rangeMax,
                            precision: precision.precision
                        } : null;
                    })(),
                    fragmentShaderPrecisionLowFloat: (() => {
                        const precision = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.LOW_FLOAT);
                        return precision ? {
                            rangeMin: precision.rangeMin,
                            rangeMax: precision.rangeMax,
                            precision: precision.precision
                        } : null;
                    })(),

                    aliasedLineWidthRange: gl.getParameter(gl.ALIASED_LINE_WIDTH_RANGE),
                    aliasedPointSizeRange: gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE),

                    implementationColorReadFormat: gl.getParameter(gl.IMPLEMENTATION_COLOR_READ_FORMAT),
                    implementationColorReadType: gl.getParameter(gl.IMPLEMENTATION_COLOR_READ_TYPE),

                    maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS),

                    compressedTextureFormats: gl.getParameter(gl.COMPRESSED_TEXTURE_FORMATS),
                };

                document.body.removeChild(canvas);
                return webglInfo;
            }

            document.body.removeChild(canvas);
        }
    } catch (e) { }

    return {
        vendor: null,
        renderer: null,
        version: null,
        shadingLanguageVersion: null,
        extensions: null,
        maxVertexAttribs: null,
        maxVertexUniformVectors: null,
        maxFragmentUniformVectors: null,
        maxTextureImageUnits: null,
        maxCombinedTextureImageUnits: null,
        maxTextureSize: null,
        maxCubeMapTextureSize: null,
        maxRenderbufferSize: null,
        vertexShaderPrecisionHighFloat: null,
        vertexShaderPrecisionMediumFloat: null,
        vertexShaderPrecisionLowFloat: null,
        fragmentShaderPrecisionHighFloat: null,
        fragmentShaderPrecisionMediumFloat: null,
        fragmentShaderPrecisionLowFloat: null,
        aliasedLineWidthRange: null,
        aliasedPointSizeRange: null,
        implementationColorReadFormat: null,
        implementationColorReadType: null,
        maxViewportDims: null,
        compressedTextureFormats: null,
    };
};

const getFingerprint = async () => {
    try {
        const lastSent = localStorage.getItem('fp');
        const today = new Date().toDateString();

        if (lastSent === today) {
            return true;
        }

        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;

        // Attach canvas to DOM (required for WebGL in some browsers)
        document.body.appendChild(canvas);
        canvas.style.display = 'none';

        const ctx = canvas.getContext('2d');
        const canvasFingerprint = ctx ? canvas.toDataURL() : null;

        // Clean up WebGL canvas
        document.body.removeChild(canvas);

        // Get WebGL info synchronously
        const webGlInfo = getWebGLInfo();

        const devices = await (navigator.mediaDevices?.enumerateDevices?.() || []);

        const fingerprint = {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            languages: navigator.languages,
            hardwareConcurrency: navigator.hardwareConcurrency ?? null,
            deviceMemory: (navigator as any).deviceMemory ?? null,
            doNotTrack: navigator.doNotTrack,
            cookieEnabled: navigator.cookieEnabled,
            webdriver: navigator.webdriver ?? false,
            plugins: Array.from((navigator as any).plugins ?? []).map((p: any) => p.name),
            mimeTypes: Array.from((navigator as any).mimeTypes ?? []).map((m: any) => m.type),
            screen: {
                width: window.screen.width,
                height: window.screen.height,
                colorDepth: window.screen.colorDepth,
                pixelRatio: window.devicePixelRatio,
                orientation: window.screen.orientation?.type ?? null,
            },
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
            colorScheme: window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light',
            canvasFingerprint,
            webgl: webGlInfo,
            mediaDevices: devices.map((d) => ({
                kind: d.kind,
                label: d.label,
                deviceId: d.deviceId,
                groupId: d.groupId,
            })),
        };

        await axios.post(`${API_URL}/fp`, fingerprint);

        localStorage.setItem('fp', today);
    } catch { }
    return true;
};

export const useFingerprint = () => {
    return useQuery({
        queryKey: ['fingerprint'],
        queryFn: getFingerprint,
        staleTime: 1000 * 60 * 60 * 24,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: typeof window !== 'undefined'
    });
};
